// The in-page authoritative server for the netcode debug harness. It runs the
// REAL match-runner loop (transport-agnostic; imported from the edge-server so we
// test the actual server logic) and speaks the real wire protocol over the
// in-process bus. It replaces the Durable Object's lobby/matchmaking with a tiny
// direct handshake: each client sends `play`, we seat it + assign a colour + send
// `assigned`, and once the expected clients have joined we build a matchConfig and
// start runMatch (which then broadcasts match_start / game_start / game_update).
import runMatch from "../../../../../edge-server/src/match-runner.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { WORM_COLOR_IDS } from "../../../shared/worm-colors";
import type { HarnessBus } from "./harness-bus";

export interface HarnessServerOptions {
  expectedClients?: number; // auto-start the match once this many have sent `play`
  mapSize?: number;
  maxScore?: number;
  // Power-up testing: powerUpDefinitions keys to NOT spawn, effect types to allow
  // (pass [] to enable everything incl. the online-disabled ones), and a spawn chance
  // override so a chosen power-up appears quickly.
  disabledPowerUps?: string[];
  disabledEffectTypes?: string[];
  powerUpSpawnChance?: number;
  // Debug: seed effects on every worm at round start (e.g. all fat for 30s to test twin-while-fat).
  debugStartEffects?: { type: string; strength: number; seconds: number }[];
  // Debug: force a switcharoonie at this game-time (seconds), to test it without a random grab.
  debugForceSwitchAt?: number | null;
  // Host-simulated bot players appended at match start (like prod's seat backfill): NOT
  // client-authoritative, simulated by the server's engine. Covers the third player type.
  serverBots?: number;
  // A Respawn round (core respawn.js): { roundTime } in seconds.
  respawn?: { roundTime: number } | null;
}

export interface HarnessServer {
  getServerGameState(): any;
  getRunner(): any;
  seats(): { clientId: string; playerId: string; name: string; colorId: string }[];
  start(): void;
  dispose(): void;
}

export function createHarnessServer(bus: HarnessBus, opts: HarnessServerOptions = {}): HarnessServer {
  const expectedClients = opts.expectedClients ?? 2;
  const size = opts.mapSize ?? 600;
  const map = gsf.createMapSquare({ name: "Square " + size, size });
  const maxScore = opts.maxScore ?? 999;

  const seats: { clientId: string; playerId: string; name: string; colorId: string }[] = [];
  let runner: any = null;
  let started = false;
  let seatSeq = 0;

  function assignColor(preferred?: string | null) {
    const used = new Set(seats.map((s) => s.colorId));
    if (preferred && WORM_COLOR_IDS.indexOf(preferred) !== -1 && !used.has(preferred)) return preferred;
    return WORM_COLOR_IDS.find((c) => !used.has(c)) || WORM_COLOR_IDS[seatSeq % WORM_COLOR_IDS.length];
  }

  function onPlay(clientId: string, msg: any) {
    if (seats.find((s) => s.clientId === clientId)) return; // already seated
    // Seat EVERY local player the client brings (real-protocol local multiplayer): one
    // playerId per entry, order preserved, all owned by the same connection.
    const lps: any[] = (Array.isArray(msg.localPlayers) && msg.localPlayers.length)
      ? msg.localPlayers
      : [{ name: msg.name, colorId: msg.colorId }];
    const playerIds = lps.map((lp) => {
      const playerId = "p" + seatSeq++;
      const colorId = assignColor(typeof lp.colorId === "string" ? lp.colorId : null);
      const name = (lp.name || "Player").slice(0, 16);
      seats.push({ clientId, playerId, name, colorId });
      return playerId;
    });
    bus.sendTo(clientId, { type: "assigned", playerIds, playerId: playerIds[0] });
    if (!started && new Set(seats.map((s) => s.clientId)).size >= expectedClients) start();
  }

  function start() {
    if (started || seats.length === 0) return;
    started = true;
    const players: any[] = seats.map((s) => ({ id: s.playerId, name: s.name, colorId: s.colorId, type: "human", left: null, right: null }));
    // Server bots: host-simulated players, exactly like prod's seat backfill (type "bot"
    // -> the engine's AI drives them; they are never externallyControlled).
    for (let i = 0; i < (opts.serverBots || 0); i++) {
      players.push({ id: "sbot" + i, name: "SrvBot " + (i + 1), colorId: assignColor(null), type: "bot", left: null, right: null, skill: 9 });
      seats.push({ clientId: "server", playerId: "sbot" + i, name: "SrvBot " + (i + 1), colorId: players[players.length - 1].colorId });
    }
    const matchConfig: any = { players, map, maxScore };
    if (opts.disabledPowerUps) matchConfig.disabledPowerUps = opts.disabledPowerUps;
    if (opts.disabledEffectTypes) matchConfig.disabledEffectTypes = opts.disabledEffectTypes;
    if (opts.powerUpSpawnChance != null) matchConfig.powerUpSpawnChance = opts.powerUpSpawnChance;
    if (opts.debugStartEffects) matchConfig.debugStartEffects = opts.debugStartEffects;
    if (opts.debugForceSwitchAt != null) matchConfig.debugForceSwitchAt = opts.debugForceSwitchAt;
    if (opts.respawn) matchConfig.respawn = opts.respawn;
    runner = runMatch({ matchConfig, send: (m: any) => bus.broadcast(m) });
  }

  bus.onServerMessage((clientId, msg) => {
    switch (msg.type) {
      case "play": return onPlay(clientId, msg);
      case "player_steering": return runner && runner.setSteering(msg.playerId, msg.steering);
      case "worm_segments": return runner && runner.applyClientSegments(msg.playerId, msg.segments);
      case "worm_spawn": {
        if (!runner) return;
        const res = runner.spawnClientWorm(msg.playerId, msg.worm);
        if (res && res.rejected) bus.sendTo(clientId, { type: "worm_rejected", wormId: msg.worm && msg.worm.wormId });
        return;
      }
      case "worm_crashed": return runner && runner.reportDeath(msg.playerId, msg.wormId);
      case "rtt_ping": return bus.sendTo(clientId, { type: "rtt_ping_ack", reqId: msg.reqId });
      case "start_game": return start();
      default: return;
    }
  });

  return {
    getServerGameState: () => (runner ? runner.getGameState() : null),
    getRunner: () => runner,
    seats: () => seats,
    start,
    dispose: () => { if (runner && runner.stop) runner.stop(); },
  };
}
